import { ButtonCus } from '@/components/common/Button';
import { Label } from '@/components/common/Label';
import FieldItemView from '@/components/general/FieldItemView';
import ImageUploadShow from '@/components/general/ImageUploadShow';
import { DATE_YMD_2, DISPPLAY_MOMENT_YMD_HM, ekDomain, ROUTES } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import { APPROVE_BIKE_STATUS, COMMON_TYPE, LANGUAGES_SUPPORTED, ROLE_OWNER, STATUS } from '@/types/enums';
import { formatDateTime } from '@/utils/datetime';
import { isPathEk } from '@/utils/routes';
import { isRoles } from '@/utils/utils';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

export default observer(function BasicInformation() {
    // hooks
    const { id } = useParams();
    const { t, i18n: { language } } = useTranslation();
    const { pathname } = useLocation();
    const navigate = useNavigate();

    // const
    const isEk = isPathEk(pathname);
    const isOwner = isRoles([...ROLE_OWNER]);

    // store
    const {
        bikeStore: { bikeDetail, getBikeDetail },
        accountStore: { accounts, getAccounts, paging: accountPaging },
        generalStore: { manufacturers, bikeTypes, bikeTypesEK, getBikeTypesEK, getBikeTypes, getManufacturers }
    } = useStore();

    // life cycle
    useEffect(() => {
        getAccounts({ isOwner: true, status: STATUS.Active }, { ...accountPaging, pagination: { ...accountPaging.pagination, pageIndex: -2 } });
        getManufacturers();
        isEk ? getBikeTypesEK() : getBikeTypes();
        if (id) {
            getBikeDetail(+id);
        }
    }, [id]);

    // function
    const handleClickEdit = () => {
        navigate(isEk ?
            `/${ekDomain}/${ROUTES.bikeManagement.hrefEK}/${ROUTES.bikeEdit.hrefEK}/${id}` :
            `${ROUTES.bikeManagement.href}/${ROUTES.bikeEdit.href}/${id}`);
    };

    const accessoryFieldsRadio: { name: string, label: string }[] = [
        { name: 'isSmartphoneHolder', label: t('field.smartphone_holder') },
        { name: 'isEtc', label: t('field.etc') },
        { name: 'isUsbCharging', label: t('field.usb_charging') },
        // { name: 'isFallOverSafetySystem', label: t('field.fall_over_safety_system') },
        { name: 'isTopBox', label: t('field.top_box') }
    ];

    return (
        <>
            <div className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-6 layout-card rounded-t-none bg-white'>
                <h3 className='font-semibold text-[1.125rem] col-span-full items-end'>{t('words_title.basic_info')}</h3>
                <FieldItemView lable={t('field.option_type')} value={bikeDetail?.type === COMMON_TYPE.GAS ? t('words_title.gas_bike') : t('words_title.ek_bike')} />
                <FieldItemView lable={t('field.owner')} value={accounts?.find(account => account.id === bikeDetail?.bikeOwnerId)?.email} />
                <FieldItemView lable={t('field.license_plate')} value={bikeDetail?.bikeLicense} />
                <FieldItemView lable={t('field.user_manual')} value={bikeDetail?.bikeManualUrl} />
                {isEk ?
                    <FieldItemView lable={t('field.rental_start_date')} value={formatDateTime(bikeDetail?.rentalStartDate, DATE_YMD_2)} /> :
                    <FieldItemView lable={t('field.chasssis_number')} value={bikeDetail?.bikeIdentification} />}
                <FieldItemView lable={t('field.rental_reservation_deadline')} value={bikeDetail?.bookingLeadTime} />
                <FieldItemView lable={t('field.model_name')} value={bikeDetail?.bikeModel} />
                <FieldItemView lable={t('field.kilometer_traveled')} value={bikeDetail?.mileage} />
                {isEk ?
                    <FieldItemView lable={t('field.color')} value={bikeDetail?.color} /> :
                    <FieldItemView lable={t('field.engine_size')} value={t(`words_title.${bikeDetail?.bikeCapacity?.code}`)} />}
                <FieldItemView lable={t('field.manufacture')} value={isEk ? bikeDetail?.bikeManufacture : manufacturers?.find(e => e.id === Number(bikeDetail?.bikeManufacture))?.[language === LANGUAGES_SUPPORTED.Japanese ? 'name_jp' : 'name']} />
                <FieldItemView lable={t('field.waiting_time_until_next_booking_hours')} value={bikeDetail?.waitingTimeUntilNextBooking} />
                {isEk ?
                    <FieldItemView lable={t('field.battery_replacement_time')} value={bikeDetail?.batteryReplacementTime} /> :
                    <FieldItemView lable={t('field.bike_type')} value={bikeTypes?.find(e => e.id === Number(bikeDetail?.bikeType))?.[language === LANGUAGES_SUPPORTED.Japanese ? 'name_jp' : 'name']} />}
                {isEk ?
                    <FieldItemView lable={t('field.bike_type')} value={bikeTypesEK?.find(e => e.id === Number(bikeDetail?.bikeType))?.[language === LANGUAGES_SUPPORTED.Japanese ? 'name_jp' : 'name']} /> :
                    <FieldItemView lable={t('field.gearbox')} value={bikeDetail?.bikeTransmission === 'at' ? t('words_title.at') : t('words_title.mt')} />}
                <FieldItemView lable={t('field.display_on_website')} value={bikeDetail?.isShowWeb ? t('field.yes') : t('field.no')} />
                <Label required className='col-span-full -mb-2 md:-mb-6'>{t('field.bike_image')}</Label>
                <div className='col-span-full grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-6'>
                    {bikeDetail?.vehicleImageBefore?.filePath && (
                        <ImageUploadShow
                            caption={t('words_title.before')}
                            urlImagePreview={bikeDetail?.vehicleImageBefore?.filePath}
                        />
                    )}
                    {bikeDetail?.vehicleImageAfter?.filePath && (
                        <ImageUploadShow
                            caption={t('words_title.after')}
                            urlImagePreview={bikeDetail?.vehicleImageAfter?.filePath}
                        />
                    )}
                    {bikeDetail?.vehicleImageLeft?.filePath && (
                        <ImageUploadShow
                            caption={t('words_title.left')}
                            urlImagePreview={bikeDetail?.vehicleImageLeft?.filePath}
                        />
                    )}
                    {bikeDetail?.vehicleImageRight?.filePath && (
                        <ImageUploadShow
                            caption={t('words_title.right')}
                            urlImagePreview={bikeDetail?.vehicleImageRight?.filePath}
                        />
                    )}
                </div>
                {isEk && (<FieldItemView classNames='col-span-full' lable={t('field.note')} value={bikeDetail?.note || '--'} textValueStyle='whitespace-pre-wrap' />)}
                {!isEk && (
                    <div className='col-span-full sm:col-span-1 xl:col-span-2 flex flex-col gap-2.5 mt-4'>
                        <h3 className='font-semibold text-[1.125rem] col-span-full'>{t('words_title.vehicle_registration_certificate')}</h3>
                        <FieldItemView lable={t('field.vehicle_registration_deadline')} value={formatDateTime(bikeDetail?.certificateExpiryDate, DATE_YMD_2)} />
                        <ImageUploadShow
                            urlImagePreview={bikeDetail?.certificateImages[0]?.filePath || ''}
                        />
                        <FieldItemView
                            lable={t('field.note')}
                            value={bikeDetail?.note || '--'}
                            textValueStyle='whitespace-pre-wrap'
                            classNames='col-span-full'
                        />
                        <span>{t('words_title.vehicle_registration_deadline_note')}</span>
                    </div>
                )}
            </div>

            <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 md:gap-6 layout-card bg-white mt-4 md:mt-6'>
                {!isEk && (
                    <>
                        <h3 className='font-semibold text-[1.125rem] col-span-full items-end'>{t('words_title.accessories')}</h3>
                        {accessoryFieldsRadio.map(({ name, label }, index) => (
                            <FieldItemView key={index} lable={label} value={bikeDetail?.[name] ? t('field.yes') : t('field.no')} />
                        ))}
                    </>
                )}
                <div className='col-span-full text-[#1A1D1F] space-y-2'>
                    <div className='flex gap-4'>
                        <label className='w-28 font-medium flex-none'>{t('field.create_by')}</label>
                        <span className='flex-auto break-all'>{bikeDetail?.creater?.mail || '--'}</span>
                    </div>
                    <div className='flex gap-4'>
                        <label className='w-28 font-medium flex-none'>{t('field.update_time')}</label>
                        <span className='flex-auto break-all'>{formatDateTime(bikeDetail?.updatedAt, DISPPLAY_MOMENT_YMD_HM) || '--'}</span>
                    </div>

                    {bikeDetail?.reasion && bikeDetail?.reasion?.length > 0 && (
                        <div className='space-y-2'>
                            <label className='font-medium'>{t('field.approve_rejeject_resion')}</label>
                            {bikeDetail?.reasion?.map((item, index) => (
                                <div key={index} className='flex gap-2 ml-4'>
                                    <span>{formatDateTime(item.createdAt, DISPPLAY_MOMENT_YMD_HM)}</span>
                                    <span>{item.approveNote || '--'}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className='mt-4 md:mt-6 flex justify-center gap-4'>
                {((!isOwner) || (isOwner && bikeDetail?.approvestatus !== APPROVE_BIKE_STATUS.Approve)) && (
                    <ButtonCus variant='default' type='button' size='sm' onClick={handleClickEdit}>{t('button.edit')}</ButtonCus>
                )}
            </div>
        </>
    );
});
